/**
 * Video formats, and the SDI data rate each one needs.
 *
 * The mapping is SMPTE, not opinion: a format determines its rate, the rate
 * determines how far coax will carry it, and that chain is why a 60 m camera
 * run is fine on an HD show and dead on a UHD one.
 *
 * A show runs at ONE format — everything is converted on the way in — so this
 * is a project setting with a per-cable override for the exceptions, rather
 * than a field on two hundred cables that nobody fills in.
 */

/** SDI rate tiers, slowest first. */
export const SDI_RATES = ['SD', 'HD', '3G', '6G', '12G'] as const
export type SdiRate = (typeof SDI_RATES)[number]

export type VideoFormat = {
  key: string
  label: string
  /** Vertical resolution, for sorting and for "is this UHD" questions. */
  lines: number
  /** Frames per second, progressive equivalent. */
  fps: number
  interlaced: boolean
  /**
   * What this format needs down a single SDI link, or **null** when it is not
   * an SDI format at all.
   *
   * Null is the honest answer for 1440p, for the high frame rates a capture
   * card quotes, and for 8K: nobody runs a broadcast show at 1440p144, and
   * inventing a rate for one would let it be chosen as a show format and then
   * drive reach and capability answers off a number SMPTE never assigned.
   * A null-rate format exists only to describe what a *device* can carry.
   */
  rate: SdiRate | null
}

/**
 * Every format this app can name.
 *
 * Two populations live here, and the difference matters:
 *
 *   - Those with a `rate` are **show formats** — what a broadcast show runs at.
 *     `SHOW_FORMATS` below is that subset, and it is what the pickers offer.
 *   - Those with a null rate exist only to record a **device ceiling**: an
 *     Elgato 4K X captures 4K144 and a 4K Pro passes 8K60 through. Recording
 *     those as "2160p60 near enough" would be a quiet lie in a catalogue whose
 *     whole discipline is published-or-nothing.
 *
 * Rate assignment follows the SMPTE mappings: ST 259 (SD, 270 Mb/s),
 * ST 292 (HD-SDI, 1.485 Gb/s), ST 424 (3G, 2.97 Gb/s), ST 2081 (6G) and
 * ST 2082 (12G). 1080p50/60 is the step that surprises people — it needs 3G,
 * where 1080p30 and 1080i50 sit happily on HD-SDI.
 */
export const VIDEO_FORMATS: VideoFormat[] = [
  { key: '576i50', label: '576i50 (PAL SD)', lines: 576, fps: 25, interlaced: true, rate: 'SD' },
  { key: '480i5994', label: '480i59.94 (NTSC SD)', lines: 480, fps: 29.97, interlaced: true, rate: 'SD' },

  { key: '720p50', label: '720p50', lines: 720, fps: 50, interlaced: false, rate: 'HD' },
  { key: '720p5994', label: '720p59.94', lines: 720, fps: 59.94, interlaced: false, rate: 'HD' },
  { key: '1080i50', label: '1080i50', lines: 1080, fps: 25, interlaced: true, rate: 'HD' },
  { key: '1080i5994', label: '1080i59.94', lines: 1080, fps: 29.97, interlaced: true, rate: 'HD' },
  { key: '1080p2398', label: '1080p23.98', lines: 1080, fps: 23.98, interlaced: false, rate: 'HD' },
  { key: '1080p24', label: '1080p24', lines: 1080, fps: 24, interlaced: false, rate: 'HD' },
  { key: '1080p25', label: '1080p25', lines: 1080, fps: 25, interlaced: false, rate: 'HD' },
  { key: '1080p2997', label: '1080p29.97', lines: 1080, fps: 29.97, interlaced: false, rate: 'HD' },
  { key: '1080p30', label: '1080p30', lines: 1080, fps: 30, interlaced: false, rate: 'HD' },

  { key: '1080p50', label: '1080p50', lines: 1080, fps: 50, interlaced: false, rate: '3G' },
  { key: '1080p5994', label: '1080p59.94', lines: 1080, fps: 59.94, interlaced: false, rate: '3G' },
  { key: '1080p60', label: '1080p60', lines: 1080, fps: 60, interlaced: false, rate: '3G' },

  { key: '2160p2398', label: '2160p23.98 (UHD)', lines: 2160, fps: 23.98, interlaced: false, rate: '6G' },
  { key: '2160p24', label: '2160p24 (UHD)', lines: 2160, fps: 24, interlaced: false, rate: '6G' },
  { key: '2160p25', label: '2160p25 (UHD)', lines: 2160, fps: 25, interlaced: false, rate: '6G' },
  { key: '2160p2997', label: '2160p29.97 (UHD)', lines: 2160, fps: 29.97, interlaced: false, rate: '6G' },
  { key: '2160p30', label: '2160p30 (UHD)', lines: 2160, fps: 30, interlaced: false, rate: '6G' },

  { key: '2160p50', label: '2160p50 (UHD)', lines: 2160, fps: 50, interlaced: false, rate: '12G' },
  { key: '2160p5994', label: '2160p59.94 (UHD)', lines: 2160, fps: 59.94, interlaced: false, rate: '12G' },
  { key: '2160p60', label: '2160p60 (UHD)', lines: 2160, fps: 60, interlaced: false, rate: '12G' },

  // ---------------------------------------------------------------------
  // Beyond single-link SDI. Device ceilings only: null rate keeps every one
  // of these out of the show-format pickers and out of the reach maths.
  // ---------------------------------------------------------------------
  { key: '1080p120', label: '1080p120', lines: 1080, fps: 120, interlaced: false, rate: null },
  { key: '1080p144', label: '1080p144', lines: 1080, fps: 144, interlaced: false, rate: null },
  { key: '1080p240', label: '1080p240', lines: 1080, fps: 240, interlaced: false, rate: null },
  { key: '1080p360', label: '1080p360', lines: 1080, fps: 360, interlaced: false, rate: null },

  { key: '1440p30', label: '1440p30 (QHD)', lines: 1440, fps: 30, interlaced: false, rate: null },
  { key: '1440p60', label: '1440p60 (QHD)', lines: 1440, fps: 60, interlaced: false, rate: null },
  { key: '1440p120', label: '1440p120 (QHD)', lines: 1440, fps: 120, interlaced: false, rate: null },
  { key: '1440p144', label: '1440p144 (QHD)', lines: 1440, fps: 144, interlaced: false, rate: null },
  { key: '1440p240', label: '1440p240 (QHD)', lines: 1440, fps: 240, interlaced: false, rate: null },

  { key: '2160p120', label: '2160p120 (UHD)', lines: 2160, fps: 120, interlaced: false, rate: null },
  { key: '2160p144', label: '2160p144 (UHD)', lines: 2160, fps: 144, interlaced: false, rate: null },

  { key: '4320p30', label: '4320p30 (8K)', lines: 4320, fps: 30, interlaced: false, rate: null },
  { key: '4320p60', label: '4320p60 (8K)', lines: 4320, fps: 60, interlaced: false, rate: null },
]

/** Every key this app can store, ceilings included. */
export const FORMAT_KEYS = VIDEO_FORMATS.map((format) => format.key)

/**
 * The formats a show can actually be set to — those with an SDI rate.
 *
 * The pickers and the `showFormat` / `formatOverride` validators use this, not
 * `VIDEO_FORMATS`. Offering 1440p144 as a show format would let someone choose
 * a setting the reach and capability checks cannot answer, and they would then
 * go quiet rather than warn — the exact failure this codebase keeps guarding
 * against.
 */
export const SHOW_FORMATS: VideoFormat[] = VIDEO_FORMATS.filter((format) => format.rate != null)

export const SHOW_FORMAT_KEYS = SHOW_FORMATS.map((format) => format.key)

const BY_KEY = new Map(VIDEO_FORMATS.map((format) => [format.key, format]))

export function formatByKey(key: string | null | undefined): VideoFormat | null {
  return key ? (BY_KEY.get(key) ?? null) : null
}

/** The rate a format needs down a single SDI link, or null if the format is unknown. */
export function rateForFormat(key: string | null | undefined): SdiRate | null {
  return formatByKey(key)?.rate ?? null
}

export const SDI_RATE_LABELS: Record<SdiRate, string> = {
  SD: 'SD-SDI (270 Mb/s)',
  HD: 'HD-SDI (1.5 Gb/s)',
  '3G': '3G-SDI (3 Gb/s)',
  '6G': '6G-SDI (6 Gb/s)',
  '12G': '12G-SDI (12 Gb/s)',
}

/**
 * What a link loses, if anything, carrying `source` into `sink`.
 *
 * Deliberately NOT a single "bigger than" comparison. Ranking formats by pixel
 * rate makes 1080p120 and 2160p30 identical — they are the same pixels per
 * second and nothing like each other — and it would let a 4K30 camera into a
 * 1080p120 capture card look like a clean link when it throws away three
 * quarters of the picture.
 *
 * So each dimension is checked on its own, and the answer says which one gives:
 * a crew can act on "it records at 1080p" or "it records at 60fps" and can do
 * nothing with "it is smaller".
 */
export type FormatShortfall = {
  /** Sink cannot carry the source's resolution. */
  resolution: boolean
  /** Sink cannot carry the source's frame rate. */
  frameRate: boolean
}

/** Null when the link carries everything the source can send. */
export function shortfall(source: VideoFormat, sink: VideoFormat): FormatShortfall | null {
  const resolution = sink.lines < source.lines
  // Interlaced formats carry their field rate as a progressive-equivalent fps,
  // so comparing the two directly would call 1080i59.94 a downgrade from
  // 1080p29.97. They are the same 29.97 here, which is the intent.
  const frameRate = sink.fps < source.fps
  return resolution || frameRate ? { resolution, frameRate } : null
}
